// client/src/components/Tasks/TaskDetailModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './CreateTaskModal.css';

function TaskDetailModal({ task, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({
    title: '',
    type: 'Phone Call',
    status: 'Pending',
    date: '',
    startTime: '',
    endTime: '',
    phoneNumber: '',
    note: ''
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const showPhoneNumber = useMemo(
    () => form.type === 'Phone Call' || form.type === 'Meeting',
    [form.type]
  );

  const getDateOnly = (value) => {
    if (!value) return '';

    const dateString = String(value).split('T')[0];
    const [year, month, day] = dateString.split('-');

    if (!year || !month || !day) return '';

    return `${year}-${month}-${day}`;
  };

  const getTimeOnly = (value) => {
    if (!value) return '';

    const timeString = String(value);
    const [hour, minute] = timeString.split(':');

    if (!hour || !minute) return '';

    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  };

  const buildUtcDateTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);

    if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }

    const localDate = new Date(year, month - 1, day, hour, minute, 0, 0);

    if (Number.isNaN(localDate.getTime())) {
      return null;
    }

    return localDate.toISOString();
  };

  useEffect(() => {
    if (!task) return;

    setForm({
      title: task.title || '',
      type: task.type || 'Phone Call',
      status: task.status || 'Pending',
      date: getDateOnly(task.due_date || task.dueDate),
      startTime: getTimeOnly(task.start_time || task.startTime),
      endTime: getTimeOnly(task.end_time || task.endTime),
      phoneNumber: task.phone_number || task.phoneNumber || '',
      note: task.note || ''
    });
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (!task?.id) {
      alert('Task ID is missing.');
      return;
    }

    const timezone = getUserTimezone();
    const dueAtUtc = buildUtcDateTime(form.date, form.startTime);
    const endAtUtc = buildUtcDateTime(form.date, form.endTime);

    const payload = {
      status: form.status || 'Pending',
      dueDate: form.date || null,
      startTime: form.startTime || null,
      endTime: form.endTime || null,
      timezone,
      dueAtUtc,
      endAtUtc,
      phoneNumber: showPhoneNumber ? form.phoneNumber : '',
      note: form.note || ''
    };

    try {
      setSaving(true);

      const res = await fetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to update task');
      }

      onUpdated?.();
      onClose?.();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Error updating task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id) {
      alert('Task ID is missing.');
      return;
    }

    const confirmed = window.confirm('Delete this task?');

    if (!confirmed) return;

    try {
      setDeleting(true);

      const res = await fetch(`/api/tasks/${task.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to delete task');
      }

      onDeleted?.();
      onClose?.();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Error deleting task');
    } finally {
      setDeleting(false);
    }
  };

  if (!task) return null;

  const isOpportunityTask = task.type === 'Opportunity' || task.opportunity_id;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal create-task-modal task-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="task-modal-header">
          <div>
            <p className="task-modal-eyebrow">
              {isOpportunityTask ? 'Opportunity Task' : 'Task'}
            </p>
            <h2>{form.title || 'Task Details'}</h2>
            <p className="task-modal-subtitle">
              Review or update task status, date, time, phone number, and notes.
            </p>
          </div>

          <button type="button" className="task-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {isOpportunityTask && (
          <div className="task-opportunity-banner">
            This task was created from an opportunity due date. Updating the task will not change the opportunity.
          </div>
        )}

        <form onSubmit={handleSave} className="task-form-grid">
          <div className="form-row full-width">
            <label>Title</label>
            <input name="title" value={form.title} readOnly />
          </div>

          <div className="form-row">
            <div className="form-column">
              <label>Type</label>
              <input name="type" value={form.type || '—'} readOnly />
            </div>

            <div className="form-column">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Rescheduled">Rescheduled</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-column">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
              />
            </div>

            <div className="form-column">
              <label>Start Time</label>
              <input
                type="time"
                name="startTime"
                value={form.startTime}
                onChange={handleChange}
              />
            </div>

            <div className="form-column">
              <label>End Time</label>
              <input
                type="time"
                name="endTime"
                value={form.endTime}
                onChange={handleChange}
              />
            </div>
          </div>

          {showPhoneNumber && (
            <div className="form-row full-width">
              <label>Phone Number</label>
              <input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>
          )}

          <div className="form-row full-width">
            <label>
              {form.type === 'Meeting'
                ? 'Meeting Notes'
                : form.type === 'Phone Call'
                  ? 'Call Notes'
                  : 'Note'}
            </label>
            <textarea
              name="note"
              value={form.note}
              onChange={handleChange}
              rows={6}
            />
          </div>

          <div className="modal-buttons task-modal-actions">
            <button
              type="button"
              className="task-delete-btn"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>

            <div className="task-action-right">
              <button type="button" onClick={onClose}>
                Cancel
              </button>

              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TaskDetailModal;